/**
 * MfaGirisAdimi — giriş sayfasının ikinci adımı: iki adımlı doğrulama kodu (backlog #640, backend #639).
 *
 * Şifre doğruysa `/login/request` oturum DEĞİL bir bilet döner (`mfa_required`); bu ekran o bilet + kodla `/login/mfa/verify`'a gider. Bilet yalnız bu
 * bileşenin prop'unda (bellekte) durur; localStorage / adres çubuğu / günlük yok.
 *   · SMS: kod maskeli numaraya gitti; "Yeniden gönder" `resend_after_seconds` kadar sayar (429'da `retry_after_seconds`); SMS hiç gitmediyse
 *     (sms_sent=false) nedeni ve — varsa — kimlik doğrulayıcı uygulamasına geçiş gösterilir, o da yoksa "yöneticiden sıfırlamasını isteyin".
 *   · Kimlik doğrulayıcı (TOTP): 6 haneli kod; SMS kullanıcısı da onaylanmış bir uygulaması varsa (`totp_fallback`) ona geçebilir (method=TOTP).
 *   · Yanlış kod: kalan hak yazılır. Kilit (429): ne zamana kadar olduğu yazılır, alan kapanır. Bilet geçersiz / süresi dolmuş: şifre ekranına dönülür.
 * Başarıda `onBasari(userId, token)` oturumu kurar (MainContext.handleMfaLoginContext) ve sayfaya girer.
 */
import React, { useState } from 'react';
import { Alert, Button, Form, Input, Label, Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import useGeriSayim from '../../../Components/Common/useGeriSayim';
import { girisKoduDogrula, girisKoduYenidenGonder } from '../../../services/mfaService';
import { kalanDakika, mfaMesaji, saatMetni } from '../Guvenlik/mfaMesajlari';

const MfaGirisAdimi = ({ sorgu, onBasari, onGeri }) => {
  const { t } = useTranslation('giris');

  // 'SMS' | 'TOTP': SMS kullanıcısı onaylı bir uygulaması varsa TOTP'ye geçebilir (ve geri dönebilir).
  const [yontem, setYontem] = useState(sorgu.method === 'SMS' ? 'SMS' : 'TOTP');
  const [kod, setKod] = useState('');
  const [yukleniyor, setYukleniyor] = useState(false);
  const [yenidenGonderiyor, setYenidenGonderiyor] = useState(false);
  const [hata, setHata] = useState(null);
  const [bilgi, setBilgi] = useState(null);
  const [kilit, setKilit] = useState(sorgu.lockedUntil);
  const [sms, setSms] = useState({
    gitti: sorgu.smsSent !== false,
    mesaj: sorgu.smsMessage,
    numara: sorgu.phoneMasked,
  });
  const [kalan, geriSayimiBaslat] = useGeriSayim(sorgu.smsSent === false ? (sorgu.retryAfterSeconds || 0) : (sorgu.resendAfterSeconds || 0));

  const smsKullaniliyor = yontem === 'SMS' && sorgu.method === 'SMS';
  const kilitDakikasi = kalanDakika(kilit);
  const kilitli = kilitDakikasi !== null;

  function biletGitti(anahtar) {
    onGeri(anahtar || 'mfa.hata.oturumGecersiz');
  }

  async function dogrula(e) {
    e.preventDefault();
    const temiz = kod.replace(/\s+/g, '');
    if (!temiz || kilitli) return;
    setYukleniyor(true);
    setHata(null);
    setBilgi(null);
    try {
      const sonuc = await girisKoduDogrula(sorgu.loginTicket, temiz, sorgu.method === 'SMS' && yontem === 'TOTP' ? 'TOTP' : undefined);
      if (sonuc.ok) {
        await onBasari(sonuc.data.user_id, sonuc.data.token);
        return;
      }
      if (sonuc.kind === 'TICKET_GONE') { biletGitti('mfa.hata.oturumGecersiz'); return; }
      if (sonuc.kind === 'LOCKED') setKilit(sonuc.lockedUntil);
      setKod('');
      setHata(mfaMesaji(t, sonuc));
    } catch (err) {
      setHata(t(err && err.network ? 'mfa.hata.baglanti' : 'mfa.hata.girisTamamlanamadi'));
    } finally {
      setYukleniyor(false);
    }
  }

  async function yenidenGonder() {
    setYenidenGonderiyor(true);
    setHata(null);
    setBilgi(null);
    try {
      const sonuc = await girisKoduYenidenGonder(sorgu.loginTicket);
      if (sonuc.ok) {
        setSms({ gitti: true, mesaj: null, numara: sonuc.data.phone_masked || sms.numara });
        geriSayimiBaslat(sonuc.data.resend_after_seconds || 60);
        setBilgi(t('mfa.giris.yeniKodGitti'));
        return;
      }
      if (sonuc.kind === 'TICKET_GONE') { biletGitti('mfa.hata.oturumGecersiz'); return; }
      if (sonuc.kind === 'TOO_SOON' && sonuc.retryAfterSeconds) geriSayimiBaslat(sonuc.retryAfterSeconds);
      if (sonuc.kind === 'LOCKED') setKilit(sonuc.lockedUntil);
      if (sonuc.kind === 'UNAVAILABLE') setSms((onceki) => ({ ...onceki, gitti: false }));
      setHata(mfaMesaji(t, sonuc));
    } catch {
      setHata(t('mfa.hata.baglanti'));
    } finally {
      setYenidenGonderiyor(false);
    }
  }

  const baslik = smsKullaniliyor ? t('mfa.giris.baslikSms') : t('mfa.giris.baslikUygulama');
  const aciklama = (() => {
    if (!smsKullaniliyor) return t('mfa.giris.aciklamaUygulama');
    if (sms.gitti) return t('mfa.giris.aciklamaSms', { numara: sms.numara || '' });
    return t('mfa.giris.smsGitmedi');
  })();

  return (
    <div className="p-2" data-testid="mfa-giris-adimi">
      <div className="text-center mt-2">
        <h5 className="text-body">{baslik}</h5>
        <p className="text-muted">{aciklama}</p>
      </div>

      {smsKullaniliyor && !sms.gitti && (
        <Alert color="warning" fade={false} data-testid="mfa-sms-gitmedi">
          {sms.mesaj && <div className="mb-1">{sms.mesaj}</div>}
          {kilitli && <div>{t('mfa.hata.kilitli', { dakika: kilitDakikasi, saat: saatMetni(kilit) || '' })}</div>}
          {!sorgu.totpFallback && <div className="mt-1">{t('mfa.giris.yoneticiyeSorun')}</div>}
        </Alert>
      )}
      {bilgi && <Alert color="success" fade={false} className="py-2">{bilgi}</Alert>}
      {hata && <Alert color="danger" fade={false} className="py-2" data-testid="mfa-hata">{hata}</Alert>}
      {kilitli && !hata && (smsKullaniliyor ? sms.gitti : true) && (
        <Alert color="warning" fade={false} className="py-2">{t('mfa.hata.kilitli', { dakika: kilitDakikasi, saat: saatMetni(kilit) || '' })}</Alert>
      )}

      <Form onSubmit={dogrula}>
        <div className="mb-3">
          <Label htmlFor="mfa-kod" className="form-label">{smsKullaniliyor ? t('mfa.giris.kodSms') : t('mfa.giris.kodUygulama')}</Label>
          <Input
            id="mfa-kod" name="code" type="text" inputMode="numeric" autoComplete="one-time-code" autoFocus
            className="form-control" maxLength={10} value={kod} disabled={kilitli || yukleniyor}
            onChange={(e) => setKod(e.target.value)}
          />
        </div>
        <Button color="primary" className="w-100" type="submit" disabled={yukleniyor || kilitli || !kod.trim()}>
          {yukleniyor ? <Spinner size="sm" /> : t('mfa.giris.dogrula')}
        </Button>
      </Form>

      <div className="d-flex flex-column align-items-center gap-2 mt-3">
        {smsKullaniliyor && (
          <Button color="link" className="p-0 text-muted" type="button" id="mfa-yeniden-gonder"
            disabled={kalan > 0 || yenidenGonderiyor || kilitli} onClick={yenidenGonder}>
            {yenidenGonderiyor ? <Spinner size="sm" />
              : kalan > 0 ? t('mfa.giris.yenidenGonderBekle', { saniye: kalan }) : t('mfa.giris.yenidenGonder')}
          </Button>
        )}
        {sorgu.method === 'SMS' && sorgu.totpFallback && (
          <Button color="link" className="p-0 text-muted" type="button" id="mfa-yontem-degistir"
            onClick={() => { setYontem(yontem === 'SMS' ? 'TOTP' : 'SMS'); setKod(''); setHata(null); setBilgi(null); }}>
            {yontem === 'SMS' ? t('mfa.giris.uygulamayaGec') : t('mfa.giris.smseGec')}
          </Button>
        )}
        <Button color="link" className="p-0 text-muted" type="button" id="mfa-geri" onClick={() => onGeri(null)}>
          {t('mfa.giris.geri')}
        </Button>
      </div>
    </div>
  );
};

export default MfaGirisAdimi;
